import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { type InventoryResource } from '@repo/api-gw-sdk';
import { Fragment } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { dayjs } from '@/utils/dayjs';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

export const BigQuerySection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;

  if (!bigQueryProps) {
    return null;
  }
  return (
    <Stack>
      <BigQueryDatasetMetadata resource={resource} />
      <BigQueryTablesSection resource={resource} />
      <BigQueryViewsSection resource={resource} />
    </Stack>
  );
};

export const BigQueryDatasetMetadata = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;

  if (!bigQueryProps) {
    return (
      <Table
        className='w-full'
        sx={{
          '& .props-col': {
            verticalAlign: 'top',
            width: '220px',
            paddingLeft: '40px',
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell
              className='props-col'
              sx={{
                borderTop: '1px solid var(--mui-palette-TableCell-border)',
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                borderTop: '1px solid var(--mui-palette-TableCell-border)',
              }}
            >
              BigQuery properties not available
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  // Check if we have empty data (feature flag disabled)
  const hasEmptyData =
    !bigQueryProps.datasetId || bigQueryProps.datasetId === '';

  if (hasEmptyData) {
    return (
      <Table
        className='w-full'
        sx={{
          '& .props-col': {
            verticalAlign: 'top',
            width: '220px',
            paddingLeft: '40px',
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell
              className='props-col'
              sx={{
                borderTop: '1px solid var(--mui-palette-TableCell-border)',
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                borderTop: '1px solid var(--mui-palette-TableCell-border)',
              }}
            >
              BigQuery discovery returned empty data
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className='props-col'>Resource Name</TableCell>
            <TableCell>{resource.resourceName}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className='props-col'>Location</TableCell>
            <TableCell>{bigQueryProps.location || 'N/A'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  const formatBytes = (bytes?: number) => {
    return bytes !== undefined && bytes !== null
      ? fileSizeFormatter(bytes)
      : '0 B';
  };

  return (
    <Table
      className='w-full'
      sx={{
        '& .props-col': {
          verticalAlign: 'top',
          width: '220px',
          paddingLeft: '40px',
        },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell className='props-col'>Storage billing model</TableCell>
          <TableCell className='capitalize'>
            {bigQueryProps.storageBillingModel?.toLowerCase() || 'N/A'}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Total logical size</TableCell>
          <TableCell>{formatBytes(bigQueryProps.totalLogicalBytes)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Total physical size</TableCell>
          <TableCell>{formatBytes(bigQueryProps.totalPhysicalBytes)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Time travel size</TableCell>
          <TableCell>
            {formatBytes(bigQueryProps.timeTravelPhysicalBytes)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Time travel window</TableCell>
          <TableCell>{bigQueryProps.timeTravelWindowDays || 7} days</TableCell>
        </TableRow>
        {bigQueryProps.cmekKeyName && (
          <TableRow>
            <TableCell className='props-col'>CMEK key</TableCell>
            <TableCell className='break-all text-xs'>
              {bigQueryProps.cmekKeyName}
            </TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell className='props-col'>Tables</TableCell>
          <TableCell>{bigQueryProps.tableCount || 0}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Views</TableCell>
          <TableCell>{bigQueryProps.viewCount || 0}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Materialized views</TableCell>
          <TableCell>{bigQueryProps.materializedViewCount || 0}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className='props-col'>Routines</TableCell>
          <TableCell>{bigQueryProps.routineCount || 0}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export const BigQueryTablesSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;

  if (!bigQueryProps?.tables || bigQueryProps.tables.length === 0) {
    return null;
  }

  const formatBytes = (bytes?: number) => {
    return bytes !== undefined && bytes !== null
      ? fileSizeFormatter(bytes)
      : '0 B';
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY HH:mm');
  };

  return bigQueryProps.tables.map((table, index) => (
    <Fragment key={table.name || index}>
      <Accordion className='px-[40px]'>
        <AccordionSummary>
          <Typography variant='h4'>
            {table.name} ({table.type === 'EXTERNAL' ? 'External' : 'Native'})
          </Typography>
        </AccordionSummary>
        <AccordionDetails className='py-[12px] pl-[36px] pr-0'>
          <Table
            className='w-1/2'
            sx={{
              td: {
                padding: '4px 0',
                borderBottom: 'none',
              },
              '& .props-col': {
                verticalAlign: 'top',
                fontWeight: '400',
                width: '140px',
              },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell className='props-col'>ID</TableCell>
                <TableCell>{table.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Type</TableCell>
                <TableCell>
                  {table.type === 'EXTERNAL'
                    ? 'External Table'
                    : 'Native Table'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Rows</TableCell>
                <TableCell>{table.numRows?.toLocaleString() || '0'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Logical size</TableCell>
                <TableCell>{formatBytes(table.totalLogicalBytes)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Active logical size</TableCell>
                <TableCell>{formatBytes(table.activeLogicalBytes)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>
                  Long-term logical size
                </TableCell>
                <TableCell>{formatBytes(table.longTermLogicalBytes)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Physical size</TableCell>
                <TableCell>{formatBytes(table.totalPhysicalBytes)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>
                  Active physical size
                </TableCell>
                <TableCell>{formatBytes(table.activePhysicalBytes)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>
                  Long-term physical size
                </TableCell>
                <TableCell>
                  {formatBytes(table.longTermPhysicalBytes)}
                </TableCell>
              </TableRow>
              {table.type === 'TABLE' && (
                <TableRow>
                  <TableCell className='props-col'>Time travel size</TableCell>
                  <TableCell>
                    {formatBytes(table.timeTravelPhysicalBytes)}
                  </TableCell>
                </TableRow>
              )}
              {table.kmsKeyName && (
                <TableRow>
                  <TableCell className='props-col'>Encryption key</TableCell>
                  <TableCell className='break-all text-xs'>
                    {table.kmsKeyName}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className='props-col'>Created</TableCell>
                <TableCell>{formatDate(table.creationTime)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='props-col'>Modified</TableCell>
                <TableCell>{formatDate(table.lastModifiedTime)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  ));
};

export const BigQueryViewsSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const bigQueryProps = resource.resourceProperties?.gcpBigQuery;

  if (!bigQueryProps) return null;

  const hasViews = bigQueryProps.views && bigQueryProps.views.length > 0;
  const hasMaterializedViews =
    bigQueryProps.materializedViews &&
    bigQueryProps.materializedViews.length > 0;
  const hasRoutines =
    bigQueryProps.routines && bigQueryProps.routines.length > 0;

  if (!hasViews && !hasMaterializedViews && !hasRoutines) {
    return null;
  }

  const formatBytes = (bytes?: number) => {
    return bytes !== undefined && bytes !== null
      ? fileSizeFormatter(bytes)
      : '0 B';
  };

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM D, YYYY HH:mm');
  };

  return (
    <Stack>
      {/* Regular Views */}
      {hasViews &&
        bigQueryProps.views!.map((view, index) => (
          <Fragment key={view.name || index}>
            <Accordion className='px-[40px]'>
              <AccordionSummary>
                <Typography variant='h4'>
                  {view.name} ({view.type})
                </Typography>
              </AccordionSummary>
              <AccordionDetails className='py-[12px] pl-[36px] pr-0'>
                <Table
                  className='w-1/2'
                  sx={{
                    td: {
                      padding: '4px 0',
                      borderBottom: 'none',
                    },
                    '& .props-col': {
                      verticalAlign: 'top',
                      fontWeight: '400',
                      width: '140px',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell className='props-col'>ID</TableCell>
                      <TableCell>{view.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Type</TableCell>
                      <TableCell>{view.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Created</TableCell>
                      <TableCell>{formatDate(view.creationTime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Modified</TableCell>
                      <TableCell>{formatDate(view.lastModifiedTime)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Fragment>
        ))}

      {/* Materialized Views */}
      {hasMaterializedViews &&
        bigQueryProps.materializedViews!.map((mv, index) => (
          <Fragment key={mv.name || index}>
            <Accordion className='px-[40px]'>
              <AccordionSummary>
                <Typography variant='h4'>
                  {mv.name} ({mv.type})
                </Typography>
              </AccordionSummary>
              <AccordionDetails className='py-[12px] pl-[36px] pr-0'>
                <Table
                  className='w-1/2'
                  sx={{
                    td: {
                      padding: '4px 0',
                      borderBottom: 'none',
                    },
                    '& .props-col': {
                      verticalAlign: 'top',
                      fontWeight: '400',
                      width: '140px',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell className='props-col'>ID</TableCell>
                      <TableCell>{mv.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Type</TableCell>
                      <TableCell>{mv.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Rows</TableCell>
                      <TableCell>
                        {mv.numRows?.toLocaleString() || '0'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Logical size</TableCell>
                      <TableCell>{formatBytes(mv.totalLogicalBytes)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>
                        Active logical size
                      </TableCell>
                      <TableCell>
                        {formatBytes(mv.activeLogicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>
                        Long-term logical size
                      </TableCell>
                      <TableCell>
                        {formatBytes(mv.longTermLogicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Physical size</TableCell>
                      <TableCell>
                        {formatBytes(mv.totalPhysicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>
                        Active physical size
                      </TableCell>
                      <TableCell>
                        {formatBytes(mv.activePhysicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>
                        Long-term physical size
                      </TableCell>
                      <TableCell>
                        {formatBytes(mv.longTermPhysicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>
                        Time travel size
                      </TableCell>
                      <TableCell>
                        {formatBytes(mv.timeTravelPhysicalBytes)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Created</TableCell>
                      <TableCell>{formatDate(mv.creationTime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Modified</TableCell>
                      <TableCell>{formatDate(mv.lastModifiedTime)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Fragment>
        ))}

      {/* Routines */}
      {hasRoutines &&
        bigQueryProps.routines!.map((routine, index) => (
          <Fragment key={routine.name || index}>
            <Accordion className='px-[40px]'>
              <AccordionSummary>
                <Typography variant='h4'>
                  {routine.name} ({routine.type})
                </Typography>
              </AccordionSummary>
              <AccordionDetails className='py-[12px] pl-[36px] pr-0'>
                <Table
                  className='w-1/2'
                  sx={{
                    td: {
                      padding: '4px 0',
                      borderBottom: 'none',
                    },
                    '& .props-col': {
                      verticalAlign: 'top',
                      fontWeight: '400',
                      width: '140px',
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell className='props-col'>ID</TableCell>
                      <TableCell>{routine.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Type</TableCell>
                      <TableCell>{routine.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Language</TableCell>
                      <TableCell>{routine.language}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Created</TableCell>
                      <TableCell>{formatDate(routine.creationTime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className='props-col'>Modified</TableCell>
                      <TableCell>
                        {formatDate(routine.lastModifiedTime)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Fragment>
        ))}
    </Stack>
  );
};
